// frontend/src/services/apiService.js
import axios from "axios";

/**
 * ============================================================================
 * Axios instance
 * ============================================================================
 */

/**
 * ============================================================================
 * API Base URL Resolution (Multi-Tenant)
 * ============================================================================
 *
 * Priority:
 *   1) window.apiConfig.apiBase        → runtime override
 *   2) import.meta.env.VITE_API_BASE   → build-time fallback
 *   3) "" (dev proxy)
 * ============================================================================
 */
const isDev = import.meta.env.DEV;
const API_BASE_URL = isDev
  ? ""
  : (window.apiConfig?.apiBase || import.meta.env.VITE_API_BASE || "");

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // required for refresh cookie
});

export { api };

/**
 * ============================================================================
 * Auth interceptors
 * ============================================================================
 */

api.interceptors.request.use((config) => {
  // ✅ If sending FormData, let the browser set multipart boundary automatically
  //    AND mark as non-retriable (multipart cannot be safely replayed)

  config.headers = config.headers || {};

  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
    delete config.headers["content-type"];

    // ⛔ Mark this request as "do not retry after 401"
    config.headers["X-No-Retry"] = "true";
  }

  const token =
    sessionStorage.getItem("accessToken") ||
    localStorage.getItem("accessToken");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

let isRefreshing = false;
let queue = [];

const flushQueue = (error, token = null) => {
  queue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  queue = [];
};

api.interceptors.response.use(
  (res) => res,

  async (error) => {
    const original = error.config;

    /**
     * ==============================================================
     * ❌ DO NOT RETRY MULTIPART / FORM-DATA REQUESTS
     *
     * Rationale:
     * - FormData objects cannot be cloned safely after token refresh.
     * - Retrying will send an empty/invalid body → DRF sees no files.
     * - This caused avatar upload failures (400: avatar required).
     * ==============================================================
     */
    if (original?.headers?.["X-No-Retry"]) {
      return Promise.reject(error);
    }

    // ✅ Handle 429 (Too Many Requests) with exponential backoff
    if (error.response?.status === 429) {
      const retryAfter =
        parseInt(error.response.headers["retry-after"] || "60") * 1000;
      const delayMs = Math.min(retryAfter, 30000); // Cap at 30s
      await new Promise((r) => setTimeout(r, delayMs));
      return api(original);
    }

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        queue.push({
          resolve: (token) => {
            original.headers.Authorization = `Bearer ${token}`;
            resolve(api(original));
          },
          reject,
        });
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.post(
        `${API_BASE_URL}/api/token/refresh/`,
        {},
        { withCredentials: true }
      );

      const access = res.data?.access;
      if (!access) throw new Error("No access token returned");

      sessionStorage.setItem("accessToken", access);
      localStorage.setItem("accessToken", access);

      flushQueue(null, access);
      original.headers.Authorization = `Bearer ${access}`;
      return api(original);
    } catch (err) {
      flushQueue(err, null);
      sessionStorage.removeItem("accessToken");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      c// Guard against redirect loops: if we're already on /login, a failed
      // refresh must not trigger another navigation. Without this, any
      // API call that 401s on the login page reloads it, remounting the
      // app, firing the call again — an infinite loop that hammers the
      // API and trips rate limiting.
      const isGuest = sessionStorage.getItem('zelospace_guest_mode') === 'true';
      const alreadyOnLogin = window.location.pathname.startsWith('/login');
      if (!isGuest && !alreadyOnLogin) {
        window.location.href = '/login?session_expired=true';
      }
      return Promise.reject(err);
    } finally {
      isRefreshing = false;
    }
  }
);

/**
 * ============================================================================
 * Auth
 * ============================================================================
 */

export const login = async (username, password) => {
  const res = await api.post("/api/login/", { username, password }, { headers: { "X-No-Retry": "true" } });
  const { access } = res.data;

  sessionStorage.setItem("accessToken", access);
  localStorage.setItem("accessToken", access);

  return res.data;
};

export const logout = async () => {
  await api.post("/api/logout/");
  sessionStorage.removeItem("accessToken");
  localStorage.removeItem("accessToken");
};

export const signUp = async (data) => {
  const res = await api.post("/api/register/", data);
  return res.data;
};

/**
 * ============================================================================
 * Email Verification
 * ============================================================================
 */

export const verifyEmail = async (token) => {
  const res = await api.post("/api/verify-email/", { token });
  return res.data;
};

export const resendVerification = async (email) => {
  const res = await api.post("/api/resend-verification/", { email });
  return res.data;
};

/**
 * ============================================================================
 * Password Reset
 * ============================================================================
 */

export const requestPasswordReset = async (email) => {
  const res = await api.post('/api/password-reset/request/', { email });
  return res.data;
};

export const verifyPasswordResetToken = async (email, token) => {
  const res = await api.post('/api/password-reset/verify/', { email, token });
  return res.data;
};

export const confirmPasswordReset = async (email, token, password) => {
  const res = await api.post('/api/password-reset/confirm/', {
    email,
    token,
    password,
  });
  return res.data;
};

/**
 * ============================================================================
 * Me (canonical identity surface)
 * ============================================================================
 */

export const fetchMe = async () => {
  const res = await api.get("/api/me/");
  return res.data;
};

/**
 * IMPORTANT: updateMe must *always* send multipart FormData.
 * It is now safe because:
 * - Requests include X-No-Retry
 * - Retry interceptor skips multipart requests
 */
export const updateMe = async (form) => {
  const res = await api.patch("/api/me/", form, {
    headers: {
      // Reinforce anti-retry flag at call-site level as well
      "X-No-Retry": "true",
    },
  });
  return res.data;
};

export const fetchMyStats = async () => {
  const res = await api.get("/api/me/stats/");
  return res.data;
};

export const fetchMySubmissions = async () => {
  const res = await api.get("/api/me/submissions/");
  return res.data;
};

/**
 * ============================================================================
 * Competitions
 * ============================================================================
 */

export const fetchCompetitions = async (params = {}) => {
  const res = await api.get("/api/competitions/", { params });
  return res.data;
};

export const fetchCompetitionDetails = async (id) => {
  const res = await api.get(`/api/competitions/${id}/`);
  return res.data;
};

/**
 * ============================================================================
 * Featured competitions (explicit surface)
 * ============================================================================
 */

export const fetchFeaturedCompetitions = async () => {
  const res = await api.get("/api/featured-competitions/");
  return res.data;
};

/**
 * ============================================================================
 * Submissions (CANONICAL)
 * ============================================================================
 */

export const fetchSubmissions = async ({ comp_id }) => {
  const res = await api.get("/api/submissions/", {
    params: { comp_id },
  });
  return res.data;
};

export const fetchSubmissionById = async (id) => {
  const res = await api.get(`/api/submissions/${id}/`);
  return res.data;
};

export const fetchSubmissionByUser = async ({ comp_id, user_id }) => {
  const res = await api.get("/api/submission-by-user/", {
    params: { comp_id, user_id },
  });
  return res.data;
};

export const createSubmission = async (payload) => {
  const res = await api.post("/api/submissions/", payload);
  return res.data;
};

export const updateSubmission = async (id, payload) => {
  const res = await api.put(`/api/submissions/${id}/`, payload);
  return res.data;
};

export const deleteSubmission = async (id) => {
  const res = await api.delete(`/api/submissions/${id}/`);
  return res.data;
};

/**
 * ============================================================================
 * Files (single canonical contract)
 * ============================================================================
 */

export const getUploadUrls = async (file) => {
  const res = await api.get("/api/get-file-url/", {
    params: {
      file_name: file.name,
      content_type: file.type,
    },
  });

  const { upload_url, file_url } = res.data;
  if (!upload_url || !file_url) {
    throw new Error("Invalid upload URL response");
  }

  return { upload_url, file_url };
};

export const deleteFile = async (fileUrl, maxRetries = 3) => {
  // ✅ Exponential backoff retry on transient failures (503, timeout, etc.)
  for (let i = 0; i < maxRetries; i++) {
    try {
      await api.delete("/api/delete-file/", {
        data: { url: fileUrl },
      });
      return;
    } catch (err) {
      const status = err.response?.status;
      const isTransient = status === 503 || status === 504 || !status; // 503, 504, or timeout
      const isLastRetry = i === maxRetries - 1;

      if (isTransient && !isLastRetry) {
        // Exponential backoff: 1s, 2s, 4s
        const delayMs = Math.pow(2, i) * 1000;
        await new Promise((r) => setTimeout(r, delayMs));
        continue;
      }

      // Final attempt failed or non-transient error
      throw err;
    }
  }
};

/**
 * ============================================================================
 * Comments
 * ============================================================================
 */

export const fetchComments = async (submission_id) => {
  const res = await api.get("/api/comments/", {
    params: { submission_id },
  });
  return res.data;
};

export const addComment = async (payload) => {
  const res = await api.post("/api/comment-submission/", payload);
  return res.data;
};

export const deleteComment = async (id) => {
  const res = await api.delete(`/api/delete-comment/${id}/`);
  return res.data;
};

/**
 * ============================================================================
 * Votes
 * ============================================================================
 */

export const fetchVotes = async (submissionId) => {
  const res = await api.get(`/api/votes/${submissionId}/`);
  return res.data;
};

export const addVote = async (payload) => {
  const res = await api.post("/api/vote-submission/", payload);
  return res.data;
};

/**
 * ============================================================================
 * Downloads
 * ============================================================================
 */

/**
 * Get a signed download URL for a single file.
 *
 * @param {number} submissionId
 * @param {string} fileUrl  - the file_url stored in the DB
 * @returns {Promise<string>} - the signed download URL
 */
export const getDownloadUrl = async (submissionId, fileUrl) => {
  const res = await api.get("/api/download-file/", {
    params: {
      submission_id: submissionId,
      file_url: fileUrl,
    },
  });
  return res.data.download_url;
};

/**
 * Get signed download URLs for ALL files in a submission.
 *
 * @param {number} submissionId
 * @returns {Promise<Array<{ file_url, download_url, file_type }>>}
 */
export const getAllDownloadUrls = async (submissionId) => {
  const res = await api.get("/api/download-file/", {
    params: { submission_id: submissionId },
  });
  return res.data.files;
};

/**
 * GET /api/tenant/
 *
 * Fetches current tenant branding and metadata.
 * TenantMiddleware resolves the tenant from the Origin header,
 * so no special URL construction needed — just a regular API call.
 */
export const getTenant = async () => {
  const res = await api.get("/api/tenant/");
  return res.data;
};

/**
 * ============================================================================
 * Community Score (ZeloScore)
 * ============================================================================
 */

/**
 * Record a unique view for a submission in a community_score competition.
 *
 * - Fire-and-forget from the caller's perspective; failures are swallowed
 *   so a missed view never disrupts the browsing experience.
 * - Backend deduplicates (time-windowed per user/session per submission).
 * - 2-second dwell is enforced client-side BEFORE this is called.
 *
 * @param {number} submissionId
 * @returns {Promise<{recorded: boolean}>}
 */
export const recordSubmissionView = async (submissionId) => {
  try {
    const res = await api.post(`/api/submissions/${submissionId}/view/`);
    return res.data;
  } catch (err) {
    // Never let a failed view tracking call surface to the user
    console.error(`Failed to record view for submission ${submissionId}`, err);
    return { recorded: false };
  }
};

/**
 * Record a unique share for a submission in a community_score competition.
 *
 * - Called when the user clicks the submission-level share button
 *   (after the deep-link URL is copied to their clipboard).
 * - Backend deduplicates (one share per user/session per submission).
 *
 * @param {number} submissionId
 * @returns {Promise<{recorded: boolean}>}
 */
export const recordSubmissionShare = async (submissionId) => {
  try {
    const res = await api.post(`/api/submissions/${submissionId}/share/`);
    return res.data;
  } catch (err) {
    console.error(`Failed to record share for submission ${submissionId}`, err);
    return { recorded: false };
  }
};

/**
 * Fetch the lightweight ZeloScore array for a community_score competition.
 *
 * Returns [{ id, zelo_score }, ...] for all approved submissions.
 * Used by the grid polling mechanism. Tiny payload, single DB query.
 *
 * @param {number} competitionId
 * @returns {Promise<Array<{id: number, zelo_score: number}>>}
 */
export const fetchCompetitionScores = async (competitionId) => {
  const res = await api.get(`/api/competitions/${competitionId}/scores/`);
  return res.data;
};// build: Wed 24 Jun 2026 16:49:30 BST
